import { Link } from "react-router-dom";
import { SiteHeader, PlatformFooter } from "../components/platform/PlatformChrome";
import { useCourses } from "../lib/courses";
import type { PublicCourse } from "@vedaunion/shared";

/**
 * Public course catalogue — `/courses`. A clean, functional catalog grid of
 * published courses. Cards carry title, level, lesson / quiz counts and a
 * short excerpt, linking to `/courses/:slug`. Product UI, not landing chrome.
 */

const LEVEL_LABEL: Record<PublicCourse["level"], string> = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

export default function PublicCourses() {
  const coursesQ = useCourses({ status: "published" });
  const courses = coursesQ.data?.courses ?? [];

  return (
    <>
      <SiteHeader />
      <main className="course-surface pt-24 sm:pt-28 pb-16">
        <div className="course-shell">
          <header className="mb-7">
            <p className="course-eyebrow course-eyebrow-violet mb-2">Courses</p>
            <h1 className="course-title">Guided learning paths</h1>
            <p className="course-subtitle mt-3">
              Step-by-step courses through the texts and the script — lessons with
              recordings and PDFs, and practice sets to check your footing as you go.
            </p>
          </header>

          {coursesQ.isLoading && (
            <p className="course-meta">Loading…</p>
          )}

          {!coursesQ.isLoading && courses.length === 0 && (
            <p className="course-meta">No courses published yet — check back soon.</p>
          )}

          {courses.length > 0 && (
            <ul className="course-grid">
              {courses.map((c) => (
                <li key={c.id} className="contents">
                  <CourseCard course={c} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      <PlatformFooter />
    </>
  );
}

function CourseCard({ course }: { course: PublicCourse }) {
  const lessonCount = course.items.filter((i) => i.kind === "lesson").length;
  const quizCount = course.items.filter((i) => i.kind === "quiz").length;
  const meta = [
    LEVEL_LABEL[course.level],
    course.estimatedMinutes ? `${course.estimatedMinutes} min` : null,
    lessonCount ? `${lessonCount} lesson${lessonCount === 1 ? "" : "s"}` : null,
    quizCount ? `${quizCount} exercise set${quizCount === 1 ? "" : "s"}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const excerpt = course.excerpt ?? course.subtitle ?? null;

  return (
    <Link to={`/courses/${course.slug}`} className="course-card">
      <div className="course-card-cover">
        {course.coverImage ? (
          <img src={course.coverImage} alt="" loading="lazy" />
        ) : (
          <span className="font-deva text-[2.75rem] text-[var(--color-ink-light)]">ॐ</span>
        )}
      </div>
      <div className="course-card-body">
        <p className="course-eyebrow course-eyebrow-violet">{LEVEL_LABEL[course.level]}</p>
        <h2 className="course-card-title line-clamp-2">{course.title}</h2>
        {excerpt && <p className="course-card-excerpt line-clamp-3">{excerpt}</p>}
        <p className="course-meta mt-auto pt-1">{meta}</p>
        <span className="course-card-cta" aria-hidden>
          View course <span className="course-card-cta-arrow">→</span>
        </span>
      </div>
    </Link>
  );
}
